import { useCallback, useEffect, useState } from 'react'
import { showToast } from '../../components/common/toast/Toast'
import { useTranslation } from 'react-i18next'
import {
  useDeleteRoleMutation,
  useLazyGetMasterRolesQuery,
  useLazyGetRoleCapabilitiesQuery,
  useLazyGetRolesQuery,
  useRecoverRoleMutation,
} from '../../api/app/UserManagementApiService'
import {
  addRoleDetails,
  addRoleAssignmentmapping,
  replaceCapabilityDetails,
} from '../../redux/reducers/rolesManagementReducer'
import { useDispatch } from 'react-redux'
import { PlatformRole } from '../../utils/types/UICommonTypes'
import { GetRolesParams } from '../../utils/types/roles/RolesTypes'

const useRoles = (isMaster: boolean) => {
  const { t } = useTranslation(['translation'])
  const dispatch = useDispatch()
  const [pageNumber, setPageNumber] = useState<number>(1) // default
  const [limit, setLimit] = useState<number>(10) // default
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [showNewRole, setShowNewRole] = useState<boolean>(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false)
  const [roleToDelete, setRoleToDelete] = useState<string>()
  const [showRecoverConfirmation, setShowRecoverConfirmation] =
    useState<boolean>(false)
  const [roleToRecover, setRoleToRecover] = useState<string>()
  const [showRoleActivity, setShowRoleActivity] = useState<boolean>(false)
  const [selectedRole, setSelectedRole] = useState<PlatformRole>()
  const [getRoleList, roleList] = useLazyGetRolesQuery()
  const [getRoleCapabilities] = useLazyGetRoleCapabilitiesQuery()
  const [deleteRole] = useDeleteRoleMutation()
  const [recoverRole] = useRecoverRoleMutation()
  const [getMasterRoleList, masterRoleList] = useLazyGetMasterRolesQuery()

  const activeRoleList = isMaster ? masterRoleList : roleList

  useEffect(() => {
    fetchRoles({
      offset: getOffsetValue(),
      limit: limit,
      searchQuery: searchQuery ?? '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMaster])

  const fetchRoles = (params: GetRolesParams) => {
    if (isMaster) {
      return getMasterRoleList(params)
    }
    return getRoleList(params)
  }

  useEffect(() => {
    fetchRoles({
      offset: getOffsetValue(),
      limit: limit,
      searchQuery: searchQuery ?? '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, limit])

  useEffect(() => {
    const getRoles = setTimeout(function () {
      fetchRoles({
        offset: getOffsetValue(),
        limit: limit,
        searchQuery: searchQuery,
      })
    }, 500)

    return () => clearTimeout(getRoles)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  const getOffsetValue = () => {
    return limit * (pageNumber - 1)
  }

  const fetchData = useCallback((page = 1) => {
    setPageNumber(page)
  }, [])

  const handlePageSize = (pageSize: number) => {
    setLimit(pageSize)
    fetchData(1)
  }

  const handleSearchQuery = (e: React.SyntheticEvent) => {
    const target = e.target as HTMLInputElement
    setSearchQuery(target.value)
    setPageNumber(1)
  }

  const handleRoleModelOpen = (status: boolean) => {
    setShowNewRole(status)
  }

  const handleRoleModelClose = (status: boolean) => {
    setShowNewRole(status)
  }

  const onDeleteHandler = ({ roleCode }: PlatformRole) => {
    setRoleToDelete(roleCode)
    setShowDeleteConfirmation(true)
  }

  const onDeleteConfirmationCancelHandler = () => {
    setShowDeleteConfirmation(false)
  }

  const displayErrorToast = (message: string, code: number) => {
    showToast(`${message}(${code})`, 'error')
  }

  const viewRoleActivity = (data: PlatformRole | undefined, show: boolean) => {
    setSelectedRole(data)
    setShowRoleActivity(show)
  }

  const onDeleteConfirmationHandler = () => {
    deleteRole(roleToDelete)
      .then((response: any) => {
        if (response?.data?.requestStatus) {
          showToast(t(`messages.${response?.data?.code}`))
        } else {
          displayErrorToast(
            t(`messages.${response?.data?.code}`),
            response?.data?.code,
          )
        }
      })
      .catch((error: any) =>
        displayErrorToast(t(`messages.${error?.status}`), error?.status),
      )
    setShowDeleteConfirmation(false)
  }

  const onRecoverHandler = ({ roleCode }: PlatformRole) => {
    setRoleToRecover(roleCode)
    setShowRecoverConfirmation(true)
  }

  const onRecoverConfirmationCancelHandler = () => {
    setShowRecoverConfirmation(false)
  }

  const onRecoverConfirmationHandler = () => {
    recoverRole(roleToRecover)
      .then((response: any) => {
        if (response?.data?.requestStatus) {
          showToast(t(`messages.${response?.data?.code}`))
        } else if (response?.error) {
          displayErrorToast(
            t(`messages.${response?.error?.status}`),
            response?.error?.status,
          )
        } else {
          displayErrorToast(
            t(`messages.${response?.data?.code}`),
            response?.data?.code,
          )
        }
      })
      .catch((error: any) =>
        displayErrorToast(t(`messages.${error?.status}`), error?.status),
      )
    setShowRecoverConfirmation(false)
  }

  const onEditHandler = ({ roleCode }: PlatformRole) => {
    fetchRoleCapabilitiesDataAsync(roleCode)
    handleRoleModelOpen(true)
  }

  async function fetchRoleCapabilitiesDataAsync(roleCode: string) {
    try {
      const roleCapabilities = await getRoleCapabilities(roleCode)
      if (roleCapabilities?.data) {
        const roleDetails = roleCapabilities?.data
        const updatedRoleDetails = {
          applicationTypeCode: roleDetails?.applicationCode,
          application: roleDetails?.applicationType,
          roleName: roleDetails?.roleName,
          copyCapabilities: roleDetails?.copyCapabilities ?? 0,
          role: roleDetails?.roleCode,
          roleDescription: roleDetails?.roleDescription,
          isEdit: true,
        }
        dispatch(addRoleAssignmentmapping(roleDetails?.roleAssignmentMapping))
        const updatedRoleCapabilities = roleDetails?.capabilities
        dispatch(addRoleDetails(updatedRoleDetails))
        dispatch(replaceCapabilityDetails(updatedRoleCapabilities))
      }
    } catch (error) {
      console.error(error)
    }
  }

  return {
    pageNumber,
    limit,
    searchQuery,
    showNewRole,
    showDeleteConfirmation,
    showRecoverConfirmation,
    roleList: activeRoleList,
    fetchData,
    handlePageSize,
    handleSearchQuery,
    handleRoleModelOpen,
    handleRoleModelClose,
    onDeleteHandler,
    onEditHandler,
    onDeleteConfirmationCancelHandler,
    onDeleteConfirmationHandler,
    onRecoverHandler,
    onRecoverConfirmationCancelHandler,
    onRecoverConfirmationHandler,
    showRoleActivity,
    selectedRole,
    viewRoleActivity,
  }
}

export default useRoles
